import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TrendingDown } from 'lucide-react';

/**
 * Build the cumulative time series used by the burndown chart.
 * Each series point is keyed by ISO date and accumulates as time advances.
 */
const buildSeries = ({ poTotal, invoices, payments }) => {
  const events = [];

  (invoices || [])
    .filter((i) => i.status !== 'cancelled')
    .forEach((i) => {
      const d = (i.created_at || '').slice(0, 10);
      if (!d) return;
      events.push({ date: d, kind: 'invoice', amount: parseFloat(i.total) || 0 });
    });

  (payments || [])
    .filter((p) => p.received)
    .forEach((p) => {
      const d = (p.date || '').slice(0, 10);
      if (!d) return;
      events.push({ date: d, kind: 'payment', amount: parseFloat(p.amount) || 0 });
    });

  if (events.length === 0) return [];

  // Sort and accumulate
  events.sort((a, b) => a.date.localeCompare(b.date));
  const dates = Array.from(new Set(events.map((e) => e.date))).sort();

  let cumInvoiced = 0;
  let cumReceived = 0;
  const points = dates.map((date) => {
    events.filter((e) => e.date === date).forEach((e) => {
      if (e.kind === 'invoice') cumInvoiced += e.amount;
      if (e.kind === 'payment') cumReceived += e.amount;
    });
    return {
      date,
      poTotal,
      invoiced: Math.round(cumInvoiced * 100) / 100,
      received: Math.round(cumReceived * 100) / 100,
      remaining: Math.max(Math.round((poTotal - cumInvoiced) * 100) / 100, 0),
    };
  });

  return points;
};

const fmt = (v) => `$${(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 0 })}`;

const StatTile = ({ label, value, sub, accent, testid }) => (
  <div className="space-y-0.5" data-testid={testid}>
    <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
    <div className={`text-lg font-bold font-mono ${accent || ''}`}>{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
  </div>
);

export const PoBudgetBurndown = ({ job, invoices }) => {
  const poTotal = parseFloat(job?.imported_from_po?.total) || 0;
  const payments = job?.progress_payments || [];

  const data = useMemo(
    () => buildSeries({ poTotal, invoices, payments }),
    [poTotal, invoices, payments],
  );

  const invoicedToDate = data.length ? data[data.length - 1].invoiced : 0;
  const receivedToDate = data.length ? data[data.length - 1].received : 0;
  const outstanding = Math.max(invoicedToDate - receivedToDate, 0);
  const remainingOnPo = Math.max(poTotal - invoicedToDate, 0);
  const overByPo = Math.max(invoicedToDate - poTotal, 0);

  const pct = (n) => (poTotal > 0 ? Math.round((n / poTotal) * 100) : 0);

  // Don't render if there's no PO and no activity
  if (!poTotal && invoicedToDate === 0 && receivedToDate === 0) return null;

  return (
    <Card className="bg-card border-border" data-testid="po-budget-burndown-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingDown className="h-5 w-5 text-primary" />
            PO Budget Burndown
          </CardTitle>
          {job?.imported_from_po?.po_number && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Tracking against PO {job.imported_from_po.po_number}
            </p>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stat row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-3 rounded-sm bg-secondary/30 border border-border">
          <StatTile
            label="PO Total"
            value={fmt(poTotal)}
            sub={poTotal ? '100% baseline' : 'no PO captured'}
            testid="bd-po-total"
          />
          <StatTile
            label="Invoiced"
            value={fmt(invoicedToDate)}
            sub={`${pct(invoicedToDate)}% of PO`}
            accent={invoicedToDate > poTotal && poTotal > 0 ? 'text-orange-400' : 'text-primary'}
            testid="bd-invoiced"
          />
          <StatTile
            label="Cash Collected"
            value={fmt(receivedToDate)}
            sub={`${pct(receivedToDate)}% of PO`}
            accent="text-green-400"
            testid="bd-received"
          />
          <StatTile
            label="Outstanding"
            value={fmt(outstanding)}
            sub="invoiced − collected"
            accent={outstanding > 0 ? 'text-yellow-400' : 'text-muted-foreground'}
            testid="bd-outstanding"
          />
          <StatTile
            label={overByPo > 0 ? 'Over PO' : 'Remaining on PO'}
            value={overByPo > 0 ? fmt(overByPo) : fmt(remainingOnPo)}
            sub={overByPo > 0 ? 'invoiced beyond PO' : 'still to bill'}
            accent={overByPo > 0 ? 'text-destructive' : 'text-muted-foreground'}
            testid="bd-remaining"
          />
        </div>

        {/* Chart */}
        {data.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-8 border border-dashed border-border rounded-sm">
            No invoices or progress payments yet — chart will populate as you bill and receive.
          </div>
        ) : (
          <div className="h-64 w-full" data-testid="po-burndown-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="date"
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)}
                />
                <Tooltip
                  contentStyle={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                  formatter={(v, name) => [fmt(v), name]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {poTotal > 0 && (
                  <ReferenceLine
                    y={poTotal}
                    stroke="hsl(var(--primary))"
                    strokeDasharray="4 4"
                    label={{
                      value: `PO ${fmt(poTotal)}`,
                      position: 'right',
                      fill: 'hsl(var(--primary))',
                      fontSize: 11,
                    }}
                  />
                )}
                <Area
                  type="monotone"
                  dataKey="invoiced"
                  name="Invoiced (cumulative)"
                  stroke="#3b82f6"
                  fill="#3b82f6"
                  fillOpacity={0.18}
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="received"
                  name="Cash collected"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PoBudgetBurndown;
